import type { ImportPreview } from './import-preview'
import type { ImportSessionState } from './import-session'

/**
 * The Import Hub progression, in order. The order IS the product contract, so
 * it is asserted as an ordered sequence rather than by presence and count.
 */
export const IMPORT_STEPS = Object.freeze([
  'upload',
  'map',
  'review',
  'import',
] as const)

export type ImportStep = (typeof IMPORT_STEPS)[number]

export type ImportStepBlockReason =
  /** Nothing has been parsed, so there is nothing to map or review. */
  | 'no_source'
  /** The source exceeded a preview budget, so no row was validated. */
  | 'source_blocked'
  /** Required destinations are unmapped, so rows cannot be validated. */
  | 'mapping_incomplete'
  /** Rows were validated and some of them failed, so there is nothing to import. */
  | 'rows_invalid'

export interface ImportStepResolution {
  /** The step actually shown. Never beyond `furthestAvailable`. */
  readonly step: ImportStep
  /** What the caller asked for, after validation. */
  readonly requestedStep: ImportStep
  /** The deepest step the current session can honestly support. */
  readonly furthestAvailable: ImportStep
  /** True only when the request could not be honoured. */
  readonly redirected: boolean
  /** Why the request was clamped; null when it was honoured. */
  readonly blockedReason: ImportStepBlockReason | null
}

export function importStepIndex(step: ImportStep): number {
  return IMPORT_STEPS.indexOf(step)
}

function asImportStep(value: unknown): ImportStep | null {
  return typeof value === 'string' &&
    (IMPORT_STEPS as readonly string[]).includes(value)
    ? (value as ImportStep)
    : null
}

/**
 * Reconcile a REQUESTED step against what the session can actually support.
 *
 * The step lives in the URL so that browser back/forward and direct entry work,
 * but the parsed source lives only in memory. A refresh, a shared link, or a
 * back-navigation after clearing the file can therefore ask for a step whose
 * evidence no longer exists. This is the one place that mismatch is resolved,
 * and it resolves it by moving BACKWARD only — never by inventing progress.
 *
 * `redirected` is precisely `step !== requestedStep`, so the UI can announce a
 * fallback exactly when one happened. An absent or unrecognised request is not
 * a thwarted request: it resolves to `upload` with `redirected: false`, because
 * announcing a redirect the user never asked for is its own lie.
 */
export function resolveImportStep(input: {
  requested: unknown
  phase: ImportSessionState['phase']
  previewStatus: ImportPreview['status'] | null
  /**
   * Whether any VISIBLE row failed validation.
   *
   * Taken separately from `previewStatus` because the preview reports a single
   * status and `preview_truncated` outranks `rows_blocked` — so a >1,000-row
   * file with a failing row reports `preview_truncated`, and reading status
   * alone would grant every step and never fire `rows_invalid`. The progression
   * must not inherit a display-ordering decision.
   */
  hasBlockedRows?: boolean
}): ImportStepResolution {
  const requestedStep = asImportStep(input.requested) ?? 'upload'

  const furthestAvailable: ImportStep = (() => {
    if (input.phase !== 'loaded') return 'upload'
    switch (input.previewStatus) {
      case 'ready':
      case 'preview_truncated':
      case 'rows_blocked':
        // rows_blocked is what Review exists to show: clamping it to Map would
        // hide the very row issues the user has to act on. A truncated preview
        // that ALSO has failing rows stops at Review for the same reason.
        return input.previewStatus === 'rows_blocked' || input.hasBlockedRows
          ? 'review'
          : 'import'
      case 'mapping_blocked':
      case 'source_blocked':
        return 'map'
      default:
        // A loaded session with no preview is an unexpected shape; fail closed
        // to upload rather than showing a step with nothing behind it.
        return 'upload'
    }
  })()

  const clamped =
    importStepIndex(requestedStep) > importStepIndex(furthestAvailable)

  // Each clamp reports its OWN cause. A fallthrough here told a user on the
  // Review step to "finish the column mapping" while the mapping panel was not
  // even on screen and the real cause — failed rows — was displayed beneath.
  const blockedReason: ImportStepBlockReason | null = !clamped
    ? null
    : input.phase !== 'loaded' || input.previewStatus === null
      ? 'no_source'
      : input.previewStatus === 'source_blocked'
        ? 'source_blocked'
        : input.previewStatus === 'mapping_blocked'
          ? // Checked BEFORE the row cause: when the mapping is incomplete no
            // row has been validated at all, so blaming rows would name a
            // consequence rather than the cause the user must act on.
            'mapping_incomplete'
          : 'rows_invalid'

  const step = clamped ? furthestAvailable : requestedStep

  return Object.freeze({
    step,
    requestedStep,
    furthestAvailable,
    redirected: step !== requestedStep,
    blockedReason,
  })
}
